import React from 'react';
import {motion, AnimatePresence} from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import {racoaptereto, examplePrompt} from '../data/racoaptereto';

const {FiX, FiInfo} = FiIcons;

const HelpModal = ({onClose}) => {
  return (
    <AnimatePresence>
      <motion.div
        initial={{opacity: 0}}
        animate={{opacity: 1}}
        exit={{opacity: 0}}
        className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 overflow-y-auto"
        onClick={onClose}
      >
        <motion.div
          initial={{opacity: 0, scale: 0.9}}
          animate={{opacity: 1, scale: 1}}
          exit={{opacity: 0, scale: 0.9}}
          className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto my-4"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 p-6 flex items-center justify-between z-10">
            <div className="flex items-center space-x-3">
              <SafeIcon icon={FiInfo} className="w-6 h-6 text-primary-blue" />
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
                Enfoque <span className="italic">InnovaPrompt</span>
              </h2>
            </div>
            <motion.button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              whileHover={{scale: 1.05}}
              whileTap={{scale: 0.95}}
              aria-label="Cerrar"
            >
              <SafeIcon icon={FiX} className="w-6 h-6 text-gray-600 dark:text-gray-400" />
            </motion.button>
          </div>
          
          <div className="p-4 sm:p-6 space-y-6">
            <div>
              <h3 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white mb-4">
                ¿Qué es <span className="italic">InnovaPrompt</span>?
              </h3>
              <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                <span className="italic">InnovaPrompt</span> es un enfoque estructurado para crear instrucciones o indicaciones (<span className="italic">prompts</span>) efectivas y completss. Cada letra allí expuesta representa un elemento clave que debe considerarse al diseñar instrucciones para inteligencia artificial. Para mayor información, consulta la presentación: <a href="http://docs.menendez.info/prompt" target="_blank" rel="noopener noreferrer" className="text-[#F54CC8] hover:underline">12 elementos clave para redactar una instrucción (<span className="italic">prompt</span> para IA): enfoque «<span className="italic">InnovaPrompt</span>»</a>.
              </p>
            </div>
            
            <div>
              <h3 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white mb-4">
                Elementos <span className="italic">InnovaPrompt</span>
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {racoaptereto.map((item, index) => (
                  <motion.div
                    key={item.key}
                    initial={{opacity: 0, x: -20}}
                    animate={{opacity: 1, x: 0}}
                    transition={{delay: index * 0.05}}
                    className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4"
                  >
                    <div className="flex items-start space-x-3">
                      <div className="w-8 h-8 bg-[#2B5596] text-white rounded-lg flex items-center justify-center font-bold text-sm flex-shrink-0" style={{
                        backgroundColor: ['rol', 'contexto', 'audiencia', 'tarea', 'resultado', 'tono'].includes(item.key) ? '#2B5596' : '#F54CC8'
                      }}>
                        {item.letter}
                      </div>
                      <div className="flex-1">
                        <h4 className="font-semibold text-gray-900 dark:text-white mb-1">
                          {item.title}
                        </h4>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                          {item.description}
                        </p>
                        <div className="bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-400 p-3 rounded-r-lg">
                          <p className="text-sm text-blue-700 dark:text-blue-400">
                            <strong>Ejemplo:</strong> {item.example}
                          </p>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
            
            <div>
              <h3 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white mb-4">
                Ejemplo completo
              </h3>
              <div className="bg-gradient-to-r from-blue-50 to-pink-50 dark:from-blue-900/20 dark:to-pink-900/20 rounded-xl p-6">
                <p className="text-gray-800 dark:text-gray-200 leading-relaxed">
                  {examplePrompt}
                </p>
              </div>
            </div>
            
            <div>
              <h3 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white mb-4">
                Consejos de uso
              </h3>
              <ul className="space-y-2 text-gray-600 dark:text-gray-400">
                <li className="flex items-start space-x-2">
                  <span className="w-2 h-2 bg-primary-blue rounded-full mt-2 flex-shrink-0"></span>
                  <span>No es necesario completar todos los campos, pero mientras más específico seas, mejores resultados obtendrás.</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="w-2 h-2 bg-primary-pink rounded-full mt-2 flex-shrink-0"></span>
                  <span>Usa el botón «Ver ejemplo» para inspirarte en cada campo.</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="w-2 h-2 bg-primary-blue rounded-full mt-2 flex-shrink-0"></span>
                  <span>El botón «Sugerir mejoras» te ayudará a optimizar tus entradas.</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="w-2 h-2 bg-primary-pink rounded-full mt-2 flex-shrink-0"></span>
                  <span>Puedes mejorar el <span className="italic">prompt</span> generado usando la función de IA integrada.</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="w-2 h-2 bg-primary-blue rounded-full mt-2 flex-shrink-0"></span>
                  <span>Haz clic en los recuadros superiores para navegar directamente a cada campo del formulario.</span>
                </li>
              </ul>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default HelpModal;